/**
 * useGetStarted — the six-step "Get Started" journey, auto-detected from real
 * data (never manually ticked: an accounting app that asks you to self-report
 * whether you sent an invoice contradicts its own promise that it knows your
 * numbers).
 *
 * ACCOUNTING-FIRST order (the founder's rule: the books must stand before any
 * transaction is urged — "get the balance sheet valued and tallied" is THE
 * onboarding deliverable, and buy-side precedes sell-side as a trader lives it):
 *   1 business       — pre-checked at signup (endowed progress: bar never at 0%)
 *   2 openings       — opening balance sheet in place OR conscious fresh start
 *   3 branding       — logo or invoice prefix set
 *   4 items          — what you buy & sell exists in the catalog
 *   5 first_purchase — first real bill / expense / payment
 *   6 first_sale     — first real invoice / receipt (dismissible: not every
 *                      business raises invoices)
 * The compliance cockpit is the COMPLETION door, not a step.
 *
 * Dismissals ("I don't need this") persist in org_config.get_started.dismissed
 * and COUNT toward completion (an unfinishable checklist is permanent guilt).
 * Completion timestamp persists in org_config.get_started.completed_at — the
 * sidebar item and hub disappear on the next session after completion.
 */
import { useCallback } from 'react';
import { supabase } from '@/db/supabase';
import { createSharedRead } from '@/shared/lib/sharedRead';
import { useAuth } from './useAuth';
import { useLiveStore } from './useLiveRefresh';
import { IN_COMPANY_ORG_TYPES } from '@/shared/config/countryConfig';
import { askCrmOnce, CRM_FILLED_EVENT } from '@/shared/crm/crmFill';

export type GetStartedStepId = 'business' | 'openings' | 'branding' | 'items' | 'first_purchase' | 'commitments' | 'first_sale' | 'connect_email' | 'connect_whatsapp' | 'roc_map' | 'modules';

export interface GetStartedStep {
    id: GetStartedStepId;
    done: boolean;
    dismissed: boolean;
    /** true = can be permanently dismissed ("I don't need this") */
    dismissible: boolean;
}

export type BusinessNatureLite = 'service' | 'trading' | 'manufacturing' | 'mixed';

export interface GetStartedState {
    loading: boolean;
    steps: GetStartedStep[];
    doneCount: number;      // done + dismissed
    total: number;
    complete: boolean;
    /** completion was recorded in a PREVIOUS session → hide guide everywhere */
    completedPreviously: boolean;
    openingExists: boolean;
    nature: BusinessNatureLite;
    refresh: () => void;
    dismiss: (id: GetStartedStepId) => Promise<void>;
    undismiss: (id: GetStartedStepId) => Promise<void>;
    /** "I'm starting from zero" — records the conscious no-openings decision */
    confirmFreshStart: () => Promise<void>;
    markCompleted: () => Promise<void>;
}

const REFRESH_EVENT = 'karr:getstarted-refresh';
export function refreshGetStarted() { window.dispatchEvent(new CustomEvent(REFRESH_EVENT)); }

async function mergeGetStartedConfig(orgId: string, patch: Record<string, unknown>) {
    // Read-modify-write so unrelated org_config keys are never clobbered.
    const { data } = await supabase.from('orgs').select('org_config').eq('id', orgId).maybeSingle();
    const cfg = (data?.org_config ?? {}) as Record<string, unknown>;
    const gs = (cfg.get_started ?? {}) as Record<string, unknown>;
    await supabase.from('orgs').update({ org_config: { ...cfg, get_started: { ...gs, ...patch } } }).eq('id', orgId);
}

type GetStartedFacts = Omit<GetStartedState, 'loading' | 'refresh' | 'dismiss' | 'undismiss' | 'confirmFreshStart' | 'markCompleted'>;

const NOT_YET: GetStartedFacts = {
    steps: [], doneCount: 0, total: 6, complete: false, completedPreviously: false, openingExists: false, nature: 'mixed',
};

async function computeGetStarted(orgId: string): Promise<GetStartedFacts> {
    const notOpening = 'dropdown_path.is.null,dropdown_path.neq.adjust.opening_balance';
    const [orgRes, saleRes, buyRes, obRes, itemRes, recurRes, emailRes, waRes] = await Promise.all([
        supabase.from('orgs').select('name, org_type, business_nature, logo_url, org_config, date_of_incorporation').eq('id', orgId).maybeSingle(),
        supabase.from('transactions').select('id', { count: 'exact', head: true }).eq('org_id', orgId).in('type', ['invoice', 'receipt']).or(notOpening),
        supabase.from('transactions').select('id', { count: 'exact', head: true }).eq('org_id', orgId).in('type', ['bill', 'expense_claim', 'payment']).or(notOpening),
        supabase.from('transactions').select('id', { count: 'exact', head: true }).eq('org_id', orgId).eq('dropdown_path', 'adjust.opening_balance'),
        supabase.from('items').select('id', { count: 'exact', head: true }).eq('org_id', orgId),
        // ga-4 "regular payments": the fixed-expenses register has a row. fixyear:
        // only a register row counts (it carries a label); an old supplier-bill
        // template from Recurring Bills used to tick this and the owner was never asked.
        supabase.from('recurring_templates').select('id', { count: 'exact', head: true })
            .eq('org_id', orgId).not('label', 'is', null),
        // ow-9 — the two channel connects. Email reads through the same RPC
        // the Settings panel uses; WhatsApp reads the ow-1 account row. Both
        // tolerate the objects not existing yet (pre-apply → step stays open).
        supabase.rpc('get_email_connections', { p_org_id: orgId }),
        supabase.from('org_wa_accounts').select('id', { count: 'exact', head: true })
            .eq('org_id', orgId).eq('status', 'active'),
    ]);

    const org = orgRes.data as { name?: string; org_type?: string; business_nature?: string; logo_url?: string; org_config?: Record<string, unknown>; date_of_incorporation?: string | null } | null;
    const cfg = (org?.org_config ?? {}) as Record<string, unknown>;
    const gs = (cfg.get_started ?? {}) as { dismissed?: string[]; completed_at?: string };
    const invoiceCfg = (cfg.invoice ?? {}) as { invoice_prefix?: string };
    const dismissed = new Set(gs.dismissed ?? []);
    const openingExists = (obRes.count ?? 0) > 0;
    // The ROC map is a company-only landmark — it draws itself once the
    // incorporation date is set (Companies Act 2013 org types only).
    const isCompany = IN_COMPANY_ORG_TYPES.has(org?.org_type ?? '');
    // crmlive: a Tohund Guide client's date is in the CRM. Ask it before
    // asking the person; a fill reloads this list (CRM_FILLED_EVENT).
    if (org && !org.date_of_incorporation) void askCrmOnce(orgId);

    // Accounting-first order: the books must STAND (opening balance sheet in
    // place or a conscious fresh-start) before daily flow is urged; buy-side
    // before sell-side, the way a trader actually lives.
    const doneById: Record<GetStartedStepId, boolean> = {
        business: !!(org?.name && org?.org_type),
        openings: openingExists || cfg.opening_confirmed === true,
        branding: !!(org?.logo_url || invoiceCfg.invoice_prefix),
        items: (itemRes.count ?? 0) > 0,
        first_purchase: (buyRes.count ?? 0) > 0,
        commitments: (recurRes.count ?? 0) > 0,
        first_sale: (saleRes.count ?? 0) > 0,
        // ow-9 — done the moment a mailbox / an own WhatsApp number is active.
        connect_email: Array.isArray(emailRes.data)
            && (emailRes.data as Array<{ is_active?: boolean }>).some((c) => c.is_active),
        connect_whatsapp: (waRes.count ?? 0) > 0,
        roc_map: !!org?.date_of_incorporation,
        // mod-4 — "Choose what you use": done the moment the org interacts with
        // the modules layer (picks a set in the gallery, or dismisses the
        // legacy nudge). New orgs are seeded at onboarding, so this is already
        // done for them; it mainly nudges older/legacy orgs.
        modules: !!cfg.modules_seeded_at,
    };
    // ow-9: the channel connects are SKIPPABLE by design (founder, 2026-08-05)
    // — Karr's own number and address work with zero setup, so these are an
    // upgrade offer, never a gate.
    // fixyear: the fixed expenses are asked up front and cannot be waved away (the founder:
    // "upfront it asks what are your fixed expenses").
    const DISMISSIBLE: GetStartedStepId[] = ['branding', 'items', 'first_purchase', 'first_sale', 'connect_email', 'connect_whatsapp', 'roc_map', 'modules'];
    const ORDER: GetStartedStepId[] = ['business', 'openings', 'branding', 'items', 'first_purchase', 'commitments', 'first_sale',
        'connect_email', 'connect_whatsapp',
        ...(isCompany ? ['roc_map' as const] : []), 'modules'];

    const steps: GetStartedStep[] = ORDER.map((id) => ({
        id, done: doneById[id], dismissed: dismissed.has(id), dismissible: DISMISSIBLE.includes(id),
    }));
    const doneCount = steps.filter((s) => s.done || s.dismissed).length;
    const complete = doneCount === steps.length;

    return {
        steps, doneCount, total: steps.length, complete,
        completedPreviously: !!gs.completed_at, openingExists,
        nature: (org?.business_nature as BusinessNatureLite) || 'mixed',
    };
}

// fastphone-2: one list per user and business for the whole app. The shell, the
// home's Needs you, the hub and Settings all read it; it was eight calls for
// each of them on every mount. A key is `<orgId>|<userId>` (what a person can
// count depends on who is asking).
const getStartedRead = createSharedRead<GetStartedFacts>((key) => computeGetStarted(key.split('|')[0]));
// Refreshed by the events that always refreshed it and by the app's live tick
// (tab focus, a change to the books), once for the app, not once per component
// that happens to be on screen.
if (typeof window !== 'undefined') {
    const onRefresh = () => getStartedRead.refresh();
    window.addEventListener(REFRESH_EVENT, onRefresh);
    window.addEventListener('karr:onboarding-refresh', onRefresh);
    window.addEventListener(CRM_FILLED_EVENT, onRefresh);
    // A test that mocks the live module without the store simply goes without.
    try { useLiveStore.subscribe(onRefresh); } catch { /* no live tick */ }
}

export function useGetStarted(orgId?: string): GetStartedState {
    const userId = useAuth((s) => s.user?.id) ?? '';
    const read = getStartedRead.use(orgId ? `${orgId}|${userId}` : null);
    const state = { ...(read.value ?? NOT_YET), loading: !!orgId && read.loading };
    const compute = useCallback(() => getStartedRead.refresh(), []);

    const dismiss = useCallback(async (id: GetStartedStepId) => {
        if (!orgId) return;
        const current = state.steps.filter((s) => s.dismissed).map((s) => s.id);
        await mergeGetStartedConfig(orgId, { dismissed: Array.from(new Set([...current, id])) });
        refreshGetStarted();
    }, [orgId, state.steps]);

    const undismiss = useCallback(async (id: GetStartedStepId) => {
        if (!orgId) return;
        const current = state.steps.filter((s) => s.dismissed).map((s) => s.id);
        await mergeGetStartedConfig(orgId, { dismissed: current.filter((d) => d !== id) });
        refreshGetStarted();
    }, [orgId, state.steps]);

    const confirmFreshStart = useCallback(async () => {
        if (!orgId) return;
        // opening_confirmed lives at org_config top level — the same key the
        // onboarding gate honours (useOnboardingStatus).
        const { data } = await supabase.from('orgs').select('org_config').eq('id', orgId).maybeSingle();
        const cfg = (data?.org_config ?? {}) as Record<string, unknown>;
        await supabase.from('orgs').update({ org_config: { ...cfg, opening_confirmed: true } }).eq('id', orgId);
        refreshGetStarted();
        window.dispatchEvent(new CustomEvent('karr:onboarding-refresh'));
    }, [orgId]);

    const markCompleted = useCallback(async () => {
        if (!orgId) return;
        await mergeGetStartedConfig(orgId, { completed_at: new Date().toISOString() });
    }, [orgId]);

    return { ...state, refresh: () => void compute(), dismiss, undismiss, confirmFreshStart, markCompleted };
}
